import {
	AuthenticationData,
	LogonResult,
	CredentialType,
	GatewayConstants,
} from './Definitions';
import { Logger } from '../Logger';
import { AotCategory, logConstants } from '../../utils/Constants';
import { HTTPFetchResponse, QueuedRequest } from '../dsAuth/data/AuthTypes';
import DSAuth from '../dsAuth/DSAuth';
import {
	gatewayConstants,
	stringConstants,
	primaryTokenStatusConstants,
} from '../AuthConstants';
import { HttpStatusCodes } from '../../utils/http/HttpConstants';
import {
	GatewaySessionParser,
	GatewaySessionResponse,
} from '../dsAuth/parser/GatewaySessionParser';
import authWebviewHandler from '../dsAuth/AuthWebViewHandler';
import AuthMan from '../AuthMan';
import { v4 as uuidv4 } from 'uuid';
import { AuthWebviewHandlerError } from '../AuthErrors';
import { HttpRequest } from '../../utils/http/Http';
import { extractCookieFromResponseHeaders } from '../../AccountConfig/core/servertype/ServerType';
import { FormsAuthenticateResponse } from '../dsAuth/data/FormsAuthenticateResponse';
import { FormsAuthenticateResponseParser } from '../dsAuth/parser/FormsAuthenticateResponseParser';
import { ParsedRequirementNode } from '../../Common/Parsers/RequirementNodeParser';
import { environment } from '../environment';
import { HeaderParser } from '../dsAuth/parser/HeaderParser';

export enum FormsState {
	Webview = 'Webview',
	Request = 'Request',
	Error = 'Error',
}
/**
 * @class which implements the forms protocol in a loop until terminal mime type response is received
 * Based on the next request, there are 3 states for a forms response
 * 		1. Webview - next request should open a webview and process it
 * 		2. Request - next request is a post request using the processed data
 * 		3. Error - error occured during processing, abort the forms protocol
 * @member formsAuthSucceeded - indicates if the forms protocol is succeeded or not
 * @member authenticationData - stores cookies and other meta data to return back from the execute function
 * @member authResponse - processed response after every POST request
 * @member nextRequest - next HTTP request to be sent if the state is Request
 * @member formsState - stores the state of the forms object
 * @member webviewStartUrl - URL to load if the state is webview
 * @member nextRequestReady - indicates whether the forms processing loop should continue or not
 */
export default class FormsProtocol {
	private formsAuthSucceeded: boolean;
	private authenticationData: AuthenticationData;
	private gatewayUrl: URL;
	private authResponse: FormsAuthenticateResponse;
	private nextRequest: HttpRequest;
	private formsState: FormsState;
	private webviewStartUrl: string;
	private nextRequestReady: boolean;

	constructor(request: HttpRequest, gatewayUrl: URL) {
		this.authenticationData = {
			result: LogonResult.Failure,
			authdata: new Map<string, string>(),
		};
		this.formsAuthSucceeded = false;
		this.nextRequest = request;
		this.formsState = FormsState.Request;
		this.gatewayUrl = gatewayUrl;
	}

	/**
	 * Executes and processes the forms protocol in a loop until the terminal mime type is received or if there is an error
	 * @returns Promise<AuthenticationData>
	 */
	public async execute(): Promise<AuthenticationData> {
		try {
			this.nextRequestReady = true;
			while (this.nextRequestReady) {
				const response = await this.sendAndReceive();
				this.nextRequestReady = false;
				await this.processServerResponse(response);
			}

			if (!this.formsAuthSucceeded) {
				Logger.getInstance().writeAot(
					AotCategory.Error,
					'Forms authentication failed, store addition will fail due to this'
				);
				throw new Error(
					'Forms authentication failed and there is no HTTP request to make'
				);
			}

			return this.authenticationData;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Forms sub-protocol failure - ${e.message}`
			);
			return this.authenticationData;
		}
	}

	/**
	 * Based on the state of the object, this function either opens a webview and processes it or sends a POST request
	 * and returns a response of type AuthenticaterResponse
	 * @returns Promise<HTTPFetchResponse>
	 */
	private async sendAndReceive(): Promise<HTTPFetchResponse> {
		try {
			let response;
			if (this.formsState === FormsState.Webview) {
				const webviewStartUrl: URL = new URL(this.webviewStartUrl);
				const id = uuidv4();

				const index = AuthMan.getInstance().requestQueue.findIndex(
					(request) => request.referenceUri === webviewStartUrl.origin
				);

				if (index != -1) {
					AuthMan.getInstance().requestQueue[index] = new QueuedRequest(
						id,
						webviewStartUrl.origin
					);
				} else {
					AuthMan.getInstance().requestQueue.push(
						new QueuedRequest(id, webviewStartUrl.origin)
					);
				}

				Logger.getInstance().write(
					logConstants.INFO,
					`Starting a webview for authentication with startUrl: ${webviewStartUrl} and id: ${id}`,
					AotCategory.Info,
					`Starting a webview for authentication with startUrl: ${webviewStartUrl}`
				);

				const webviewResponse = await authWebviewHandler.start(
					webviewStartUrl.href,
					webviewStartUrl.origin,
					id,
					stringConstants.AUTHWEBVIEW,
					id,
					true
				);

				const requestIndex = AuthMan.getInstance().requestQueue.findIndex(
					(request) => request.referenceUri === webviewStartUrl.origin
				);
				AuthMan.getInstance().requestQueue.splice(requestIndex, 1);

				response = this.processWebviewResponseAndHandlePostback(webviewResponse);
			} else if (this.formsState === FormsState.Request) {
				this.nextRequest.headers.updateHeader('Connection', 'Close');
				response = await DSAuth.getInstance().fetch(
					this.nextRequest.method,
					this.nextRequest.url.href,
					this.nextRequest.headers.toJSON(),
					this.nextRequest.body,
					this.nextRequest.url.origin,
					true,
					''
				);
			} else {
				Logger.getInstance().write(
					logConstants.INFO,
					`Received unexpected response from forms`
				);
				this.authenticationData.result = LogonResult.ProtocolError;
				throw new Error('Received unexpected response from forms');
			}

			return response;
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Failed to fetch the forms start path - ${e.message}`
			);
			throw e;
		}
	}

	/**
	 * Processes the server response based on the status code of the response
	 * @param response {HTTPFetchResponse}
	 * @returns
	 */
	private async processServerResponse(response: HTTPFetchResponse): Promise<void> {
		try {
			if (response.statusCode === HttpStatusCodes.HTTP_STATUS_OK) {
				await this.http200Received(response);
			} else if (
				response.statusCode === HttpStatusCodes.HTTP_STATUS_DENIED ||
				response.statusCode === HttpStatusCodes.HTTP_STATUS_FORBIDDEN
			) {
				this.http401or403Received(response);
			} else {
				this.authenticationData.result = LogonResult.NetworkError;
				throw new Error(
					'Unexpected server response for forms start path: ' + response.statusCode
				);
			}
		} catch (e) {
			throw e;
		}

		return;
	}

	/**
	 * Given a HTTPFetchResponse, parses it to FormsAuthenticateResponse type and sets the state and meta data for the next request
	 * @param response HTTPFetchResponse - from sendAndReceive
	 */
	private async processForm(response: HTTPFetchResponse): Promise<void> {
		const authResponseParser = new FormsAuthenticateResponseParser();
		this.authResponse = authResponseParser.parseXML(
			response.body
		) as FormsAuthenticateResponse;

		const requirementsArray = this.authResponse.getRequirements();
		let requirement: ParsedRequirementNode;
		if (
			(requirement = this.extractRequirementNodeFromCredentialType(
				requirementsArray,
				'webview'
			))
		) {
			this.formsState = FormsState.Webview;
			this.webviewStartUrl = requirement.credential.webViewData.startUrl;
		} else if (
			(requirement = this.extractRequirementNodeFromCredentialType(
				requirementsArray,
				'none'
			))
		) {
			this.formsState = FormsState.Request;
			this.nextRequest.method = 'POST';
			this.nextRequest.url = new URL(
				this.gatewayUrl.origin + this.authResponse.getPostBack()
			);
			this.nextRequest.headers.updateHeader(
				'Content-Type',
				GatewayConstants.FormsURLEncoded
			);
			this.nextRequest.headers.updateHeader(
				'Cookie',
				`${GatewayConstants.NSC_AAAC}=${this.authenticationData.authdata.get(
					GatewayConstants.NSC_AAAC
				)}`
			);
			this.nextRequest.body = `${GatewayConstants.StateContext}=${
				this.authResponse.getStateContext() ?? ''
			}&${requirement.credential.id}=${requirement.credential.value}`;
		} else {
			this.authenticationData.result = LogonResult.ProtocolError;
			this.formsState = FormsState.Error;
		}
	}

	/**
	 * Processing the response if status code 200 is received, checks if response type is of terminal mime type and processes the gatewaysession response
	 * else continues to process if the type is authenticateresponse
	 * @param response
	 * @returns
	 */
	private async http200Received(response: HTTPFetchResponse): Promise<void> {
		if (this.receivedTerminalMimeType(response)) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Received terminal mimetype for gateway logon`
			);

			this.processGatewaySessionResponse(response);
			this.nextRequestReady = false;
			return;
		}

		if (!this.checkIfFormsMimeType(response)) {
			this.nextRequestReady = false;
			this.authenticationData.result = LogonResult.FormsMimeTypeNotReceived;
			throw new Error(
				'Received unexpected content type -' + response.headers['Content-Type']
			);
		}

		await this.processForm(response);
		this.nextRequestReady = true;
	}

	/**
	 * Given a HTTPFetchResponse, parse it to GatewaySessionResponse type and extract the metadata about the gateway session
	 * @param response - HTTPFetchResponse of type GatewaySessionResponse
	 */
	private processGatewaySessionResponse(response: HTTPFetchResponse): void {
		Logger.getInstance().write(
			logConstants.INFO,
			`Forms authentication succeeded, saving the metadata`,
			AotCategory.Info,
			`Forms authentication succeeded, saving the metadata and endpoints`
		);

		this.formsAuthSucceeded = true;
		this.authenticationData.result = LogonResult.Success;

		const gatewaySessionResponse: GatewaySessionResponse =
			GatewaySessionParser.getInstance().parseXML(response.body);

		this.authenticationData.authdata.set(
			'UrlRewriteRule',
			gatewaySessionResponse.GatewaySession.SessionInformation.UrlRewriteRule
		);
		this.authenticationData.authdata.set(
			'FullVpn',
			gatewaySessionResponse.GatewaySession.SessionInformation.FullVpn
		);
		this.authenticationData.authdata.set(
			'SecureBrowsePrefix',
			gatewaySessionResponse.GatewaySession.SessionInformation.SecureBrowsePrefix
		);
		this.authenticationData.authdata.set(
			'LogOff',
			gatewaySessionResponse.GatewaySession.SessionInformation.LogOff
		);
		this.authenticationData.authdata.set(
			'CredentialDisplayName',
			gatewaySessionResponse.GatewaySession.CredentialUpdateInformationList
				.CredentialUpdateInformation.CredentialDisplayName
		);
		this.authenticationData.authdata.set(
			'PasswordChange',
			gatewaySessionResponse.GatewaySession.CredentialUpdateInformationList
				.CredentialUpdateInformation.PasswordChange
		);
		this.authenticationData.authdata.set(
			'CredentialUpdateService',
			gatewaySessionResponse.GatewaySession.CredentialUpdateInformationList
				.CredentialUpdateInformation.CredentialUpdateService
		);
	}

	/**
	 * Checks if the HTTPFetchResponse is of type authenticateresponse or gatewaysessionresponse
	 * @param response HTTPFetchResponse
	 * @returns boolean
	 */
	private checkIfFormsMimeType(response: HTTPFetchResponse): boolean {
		const contentTypeHeader = HeaderParser.getInstance().extractHeaderValue(
			response.headers,
			'Content-Type'
		);
		return contentTypeHeader.includes(gatewayConstants.AUTHENTICATE_RESPONSE);
	}

	/**
	 * Checks if the HTTPFetchResponse is of terminal mime type
	 * @param response HTTPFetchResponse
	 * @returns boolean
	 */
	private receivedTerminalMimeType(response: HTTPFetchResponse): boolean {
		const contentTypeHeader = HeaderParser.getInstance().extractHeaderValue(
			response.headers,
			'Content-Type'
		);
		return contentTypeHeader.includes(gatewayConstants.GATEWAY_TERMINAL_MIMETYPE);
	}

	/**
	 * Terminates the loop if 401 or 403 status code is received from the response
	 * @param response HTTPFetchResponse
	 */
	private http401or403Received(response: HTTPFetchResponse): void {
		Logger.getInstance().write(
			logConstants.INFO,
			`Received unexpected response from server` + response
		);
		this.nextRequestReady = false;
		this.formsAuthSucceeded = false;
		this.authenticationData.result = LogonResult.AccessDenied;
	}

	/**
	 * Processes the webview response and handles the postback depending on the value
	 * @param response - webview response received from native
	 * @returns Promise<HTTPFetchResponse>
	 */
	private async processWebviewResponseAndHandlePostback(
		response: string
	): Promise<HTTPFetchResponse> {
		if (response === undefined) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Received unexpected response from webview: ${response}`,
				AotCategory.Error,
				`Store addition failed - Received unexpected response from webview`
			);
			this.authenticationData.result = LogonResult.ProtocolError;
			throw new Error('Received unexpected response from webview');
		}

		let postbackResponse: HTTPFetchResponse;
		if (response === primaryTokenStatusConstants.ERROR_CANCEL_LOGIN) {
			Logger.getInstance().write(
				logConstants.INFO,
				`Entered wrong credentials or login cancelled`,
				AotCategory.Warning,
				`User entered wrong credentials or cancelled login, navigating to add account page or re-authenticating`
			);
			postbackResponse = await DSAuth.getInstance().fetch(
				'GET',
				this.gatewayUrl.origin + this.authResponse.getCancelPostback(),
				{
					Accept:
						gatewayConstants.AUTHENTICATE_RESPONSE +
						',' +
						gatewayConstants.GATEWAY_SESSION_RESPONSE,
					Host: this.gatewayUrl.hostname,
					'x-Citrix-Gateway': this.gatewayUrl.hostname,
					'X-Citrix-AM-LabelTypes': gatewayConstants.LABEL_TYPES,
					'X-Citrix-AM-CredentialTypes': gatewayConstants.CREDENTIAL_TYPES,
					'Cache-Control': 'no-cache',
					Pragma: 'no-cache',
				},
				'',
				'',
				true,
				''
			);
			await environment.deleteWebViewCookies();
			this.authenticationData.result = LogonResult.CancelledByUser;
			throw new AuthWebviewHandlerError('Login was cancelled');
		} else {
			const requirements = this.authResponse.getRequirements();
			const requirement: ParsedRequirementNode =
				this.extractRequirementNodeFromCredentialType(requirements, 'webview');
			const postData = `StateContext=&${requirement.credential.id}=${response}`;
			postbackResponse = await DSAuth.getInstance().fetch(
				'POST',
				this.gatewayUrl.origin + this.authResponse.getPostBack(),
				{
					Host: this.gatewayUrl.hostname,
					'X-Citrix-AM-LabelTypes': gatewayConstants.LABEL_TYPES,
					'X-Citrix-AM-CredentialTypes': gatewayConstants.CREDENTIAL_TYPES,
					'X-Citrix-Gateway': this.gatewayUrl.hostname,
					Accept:
						'application/vnd.citrix.authenticateresponse-1+xml, application/vnd.citrix.gatewaysession-1+xml',
					'Accept-Language': 'en-US', // TODO - change according to locale
					'Content-Type': 'application/x-www-form-urlencoded',
					Connection: 'Close',
				},
				postData,
				this.gatewayUrl.origin,
				true,
				''
			);

			this.authenticationData.authdata.set(
				'NSC_AAAC',
				extractCookieFromResponseHeaders(postbackResponse.headers, 'NSC_AAAC').value
			);

			return postbackResponse;
		}
	}

	/**
	 * Function to extract requirement node from parsed requirement nodes given the credential type
	 * @param requirements - array of parsed requirement nodes
	 * @param credentialType - credential type of the requirement node to be extracted
	 * @returns
	 */
	private extractRequirementNodeFromCredentialType(
		requirements: ParsedRequirementNode[],
		credentialType: CredentialType
	): ParsedRequirementNode {
		for (const requirement of requirements) {
			if (requirement.credential.type === credentialType) return requirement;
		}
		return null;
	}
}
